import type { RequestListItem } from "@gw/shared-types";
import { fmtETDate } from "@/lib/format";
import { SLOWNESS_CONCEPT } from "@/lib/labels";
import { useKeyboardRowNav } from "@/lib/useKeyboardRowNav";
import { SortHead, type SortState } from "./SortHead";
import { SlownessPill } from "./SlownessPill";
import { StatusPill } from "./StatusPill";
import { BottleneckPill } from "./BottleneckPill";

interface Props {
  rows: RequestListItem[];
  sort: SortState;
  onSortChange: (s: SortState) => void;
  onRowClick: (id: string) => void;
  unit: "bd" | "cd";
}

const TASK_IDLE_TIP =
  "Idle time across the request's tasks — dead time before each task opened (gaps between tasks, " +
  "re-issues, weekends/holidays). Summed over completed, non-discarded tasks. Matches Total Task Idle on the detail page.";
const TASK_WAIT_TIP =
  "Wait time across the request's tasks (assigned → started). Summed over completed, non-discarded " +
  "tasks. Matches Total Task Wait on the detail page.";
const TASK_EXEC_TIP =
  "Execution time across the request's tasks (started → completed active time). Summed over " +
  "completed, non-discarded tasks. Matches Total Task Execution on the detail page.";
const TASK_DUR_TIP =
  "Idle + Wait + Execution across the request's tasks. Matches Total Task Duration on the detail page.";
const PRE_ONBOARDING_TIP =
  "Time from the linked pre-verification request's start to when the work order was created. Only " +
  "applies to onboardings that originated from a pre-verification request — otherwise shows —.";

/**
 * Stacked bd/cd value cell, matching the Total dur. column: primary value in the active unit, the
 * other unit muted below. Renders "—" when the request has no task totals.
 */
function TaskDurCell({
  bd,
  cd,
  unit,
}: {
  bd: number | null;
  cd: number | null;
  unit: "bd" | "cd";
}) {
  if (bd == null || cd == null) {
    return <span style={{ color: "var(--fg-3)" }}>—</span>;
  }
  const primary = unit === "cd" ? cd : bd;
  const secondary = unit === "cd" ? bd : cd;
  const primaryUnit = unit === "cd" ? "cd" : "bd";
  const secondaryUnit = unit === "cd" ? "bd" : "cd";
  return (
    <div className="dur-stack">
      <span className="dur-bd">
        {primary.toFixed(2)} {primaryUnit}
      </span>
      <span className="dur-cd">
        {secondary.toFixed(2)} {secondaryUnit}
      </span>
    </div>
  );
}

export function InstancesTable({ rows, sort, onSortChange, onRowClick, unit }: Props) {
  const { getRowProps } = useKeyboardRowNav(rows.length, (i) => {
    const row = rows[i];
    if (row) onRowClick(row.id);
  });

  if (rows.length === 0) {
    return (
      <div className="tbl-wrap" style={{ maxHeight: "calc(100vh - 470px)" }}>
        <div className="empty">
          <h4>No matching requests</h4>
          <p>Try adjusting filters or expanding the date range.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tbl-wrap" style={{ maxHeight: "calc(100vh - 470px)" }}>
      <table className="tbl">
        <thead>
          <tr>
            <SortHead label="Slowness" sortKey="slowness" sort={sort} setSort={onSortChange} info={SLOWNESS_CONCEPT} className="col-pin col-pin-1" />
            <SortHead label="Worker" sortKey="worker" sort={sort} setSort={onSortChange} className="col-pin col-pin-2" />
            <SortHead label="Client" sortKey="client" sort={sort} setSort={onSortChange} className="col-pin col-pin-3 col-pin-last" />
            <SortHead label="Type" sortKey="requestType" sort={sort} setSort={onSortChange} />
            <SortHead label="LOB" sortKey="lineOfBusiness" sort={sort} setSort={onSortChange} />
            <SortHead label="Eng. type" sortKey="engagementType" sort={sort} setSort={onSortChange} />
            <SortHead label="Assigned to" sortKey="assignedToUserName" sort={sort} setSort={onSortChange} />
            <th>Status</th>
            <SortHead label="Pre-Verification" sortKey="preOnboardingDuration" sort={sort} setSort={onSortChange} info={PRE_ONBOARDING_TIP} numeric />
            <SortHead label="Task idle" sortKey="taskIdle" sort={sort} setSort={onSortChange} info={TASK_IDLE_TIP} numeric />
            <SortHead label="Task wait" sortKey="taskWait" sort={sort} setSort={onSortChange} info={TASK_WAIT_TIP} numeric />
            <SortHead label="Task exec." sortKey="taskExecution" sort={sort} setSort={onSortChange} info={TASK_EXEC_TIP} numeric />
            <SortHead label="Task dur." sortKey="taskDuration" sort={sort} setSort={onSortChange} info={TASK_DUR_TIP} numeric />
            <SortHead label="Created" sortKey="created" sort={sort} setSort={onSortChange} />
            <SortHead label="Approved" sortKey="approved" sort={sort} setSort={onSortChange} />
            <SortHead label="Total dur." sortKey="duration" sort={sort} setSort={onSortChange} numeric />
            <th>Bottleneck</th>
            <th>Rework</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr
              key={r.id}
              className={"slowness-" + r.slownessBucket.toLowerCase()}
              onClick={() => onRowClick(r.id)}
              {...getRowProps(i)}
            >
              <td className="col-pin col-pin-1">
                <SlownessPill
                  bucket={r.slownessBucket}
                  ratio={r.slownessRatio}
                  peerMedian={r.peerMedian}
                  peerCount={r.peerCount}
                  unit={unit}
                />
              </td>
              <td className="col-pin col-pin-2">
                <div className="cell-primary">{r.workerName}</div>
                <div className="cell-secondary">{r.workOrderNumber}</div>
              </td>
              <td className="col-pin col-pin-3 col-pin-last">{r.client}</td>
              <td>
                <span className="cell-secondary">{r.requestType}</span>
              </td>
              <td>
                <span className="cell-secondary">{r.lineOfBusiness}</span>
              </td>
              <td>
                <span className="cell-secondary">{r.engagementType}</span>
              </td>
              <td>
                <span className="cell-secondary">{r.assignedToUserName}</span>
              </td>
              <td>
                {r.calculatedStatus === "Completed" || r.calculatedStatus === "InProgress" ? (
                  <StatusPill status={r.calculatedStatus} />
                ) : (
                  <span style={{ color: "var(--fg-3)" }}>—</span>
                )}
              </td>
              <td className="numeric">
                <TaskDurCell bd={r.preOnboardingDurationBd} cd={r.preOnboardingDurationCd} unit={unit} />
              </td>
              <td className="numeric">
                <TaskDurCell bd={r.taskIdleBd} cd={r.taskIdleCd} unit={unit} />
              </td>
              <td className="numeric">
                <TaskDurCell bd={r.taskWaitBd} cd={r.taskWaitCd} unit={unit} />
              </td>
              <td className="numeric">
                <TaskDurCell bd={r.taskExecBd} cd={r.taskExecCd} unit={unit} />
              </td>
              <td className="numeric">
                <TaskDurCell bd={r.taskDurationBd} cd={r.taskDurationCd} unit={unit} />
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                <span className="cell-secondary">{fmtETDate(r.requestCreatedDateTime)}</span>
              </td>
              <td style={{ whiteSpace: "nowrap" }}>
                {r.workOrderApproveDatetime ? (
                  <span className="cell-secondary">{fmtETDate(r.workOrderApproveDatetime)}</span>
                ) : r.workOrderId == null ? (
                  <span style={{ color: "var(--fg-3)" }}>N/A</span>
                ) : (
                  <span className="dur-running">In progress</span>
                )}
              </td>
              <td className="numeric">
                {r.totalBusinessDays != null && r.totalCalendarDays != null ? (
                  <div className="dur-stack">
                    {unit === "cd" ? (
                      <>
                        <span className="dur-bd">{r.totalCalendarDays.toFixed(2)} cd</span>
                        <span className="dur-cd">{r.totalBusinessDays.toFixed(2)} bd</span>
                      </>
                    ) : (
                      <>
                        <span className="dur-bd">{r.totalBusinessDays.toFixed(2)} bd</span>
                        <span className="dur-cd">{r.totalCalendarDays.toFixed(2)} cd</span>
                      </>
                    )}
                  </div>
                ) : (
                  <span className="dur-running">running</span>
                )}
              </td>
              <td>
                <BottleneckPill stage={r.bottleneckStage} unit={unit} />
              </td>
              <td>
                {r.workOrderRejectCount > 0 ? (
                  <span
                    className="rework-icon"
                    title={`${r.workOrderRejectCount} reject(s) · ${r.workOrderSubmitCount} submit(s)`}
                  >
                    <span className="material-symbols-rounded" style={{ fontSize: 14 }}>
                      refresh
                    </span>
                    {r.workOrderRejectCount}
                  </span>
                ) : (
                  <span style={{ color: "var(--fg-3)" }}>—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
